import { useEffect } from "react";
import { useRouterState } from "@tanstack/react-router";

import { TRACKING } from "@/lib/tracking-config";
import { onConsentChange, readConsent, type Consent } from "@/lib/consent";

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
    fbq?: ((...args: unknown[]) => void) & { queue?: unknown[]; loaded?: boolean; version?: string; callMethod?: (...args: unknown[]) => void; push?: unknown };
    _fbq?: unknown;
    clarity?: (...args: unknown[]) => void;
    hj?: ((...args: unknown[]) => void) & { q?: unknown[] };
    _hjSettings?: { hjid: number; hjsv: number };
  }
}

function addScript(id: string, src: string) {
  if (document.getElementById(id)) return;
  const s = document.createElement("script");
  s.id = id;
  s.async = true;
  s.src = src;
  document.head.appendChild(s);
}

function addInline(id: string, code: string) {
  if (document.getElementById(id)) return;
  const s = document.createElement("script");
  s.id = id;
  s.text = code;
  document.head.appendChild(s);
}

/** Aplica o consentimento: Google Consent Mode + fbq('consent') + carrega tags permitidas. */
function applyConsent(c: Consent | null) {
  const analytics = c?.analytics === true;
  const marketing = c?.marketing === true;
  const { clarityProjectId, metaPixelId, ga4MeasurementId, gtmContainerId, hotjarSiteId } = TRACKING;

  window.gtag?.("consent", "update", {
    analytics_storage: analytics ? "granted" : "denied",
    ad_storage: marketing ? "granted" : "denied",
    ad_user_data: marketing ? "granted" : "denied",
    ad_personalization: marketing ? "granted" : "denied",
  });

  if (analytics) {
    if (ga4MeasurementId) {
      addScript("ga4-lib", `https://www.googletagmanager.com/gtag/js?id=${ga4MeasurementId}`);
      if (!document.getElementById("ga4-config")) {
        addInline("ga4-config", "/* ga4 config */");
        window.gtag?.("config", ga4MeasurementId, { send_page_view: false });
      }
    }
    if (clarityProjectId) {
      addInline(
        "clarity-init",
        `(function(c,l,a,r,i,t,y){c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);})(window,document,"clarity","script","${clarityProjectId}");`,
      );
    }
    if (hotjarSiteId) {
      addInline(
        "hotjar-init",
        `(function(h,o,t,j,a,r){h.hj=h.hj||function(){(h.hj.q=h.hj.q||[]).push(arguments)};h._hjSettings={hjid:${hotjarSiteId},hjsv:6};a=o.getElementsByTagName('head')[0];r=o.createElement('script');r.async=1;r.src=t+h._hjSettings.hjid+j+h._hjSettings.hjsv;a.appendChild(r);})(window,document,'https://static.hotjar.com/c/hotjar-','.js?sv=');`,
      );
    }
  }

  if ((analytics || marketing) && gtmContainerId) {
    addInline(
      "gtm-init",
      `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','${gtmContainerId}');`,
    );
  }

  if (metaPixelId) {
    if (marketing) {
      if (!window.fbq) {
        addInline(
          "meta-pixel-init",
          `!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');fbq('consent','grant');fbq('init','${metaPixelId}',{},{agent:'lovable'});fbq('track','PageView');`,
        );
      } else {
        window.fbq("consent", "grant");
      }
    } else if (window.fbq) {
      window.fbq("consent", "revoke");
    }
  }
}

export function Tracking() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  useEffect(() => {
    applyConsent(readConsent());
    return onConsentChange(applyConsent);
  }, []);

  // Page views em mudanças de página (apenas com consentimento)
  useEffect(() => {
    const c = readConsent();
    if (c?.analytics && TRACKING.ga4MeasurementId && window.gtag) {
      window.gtag("event", "page_view", { page_path: pathname });
    }
    if (c?.marketing && TRACKING.metaPixelId && window.fbq) {
      window.fbq("track", "PageView");
    }
  }, [pathname]);

  return null;
}
